
import { useCallback, useEffect, useState } from 'react';
import { DeviceDetailsModal } from '@/components/admin/DeviceDetailsModal';
import { Button, Checkbox, Input } from '@/components/ui';
import { api } from '@/lib/api';
import { AppSettings } from '@/hooks/useSettings';
import { cn } from '@/lib/utils';

export interface OffenderSummary {
  deviceId: string;
  reportCount: number;
  submissionCount: number;
  lastSeen: string | null;
  relatedIps: string[];
  isBlocked: boolean;
  blockedSubmitterId: string | null;
}

export interface BlockedSubmitter {
  id: string;
  deviceId: string;
  ipAddress: string | null;
  reason: string | null;
  isAutoBlock: boolean;
  blockedByUserId: string | null;
  blockedAt: string;
  notes: string | null;
}

function truncateDeviceId(id: string, len = 12): string {
  if (id.length <= len + 3) return id;
  return `${id.slice(0, len)}…`;
}

const detailsButtonClassName =
  'text-sky-600 hover:text-sky-700 hover:bg-sky-50 dark:text-sky-400 dark:hover:text-sky-300 dark:hover:bg-sky-900/30';

interface ModerationSettingsProps {
  settings: AppSettings;
  isSaving: boolean;
  onSave: (partial: Partial<AppSettings>) => Promise<boolean>;
}

export function ModerationSettings({
  settings,
  isSaving,
  onSave,
}: ModerationSettingsProps) {
  const [autoBlockEnabled, setAutoBlockEnabled] = useState(
    settings.autoBlockEnabled ?? false
  );
  const [autoBlockReportThreshold, setAutoBlockReportThreshold] = useState(
    String(settings.autoBlockReportThreshold ?? 3)
  );
  const [offenders, setOffenders] = useState<OffenderSummary[]>([]);
  const [blocks, setBlocks] = useState<BlockedSubmitter[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  const loadModerationData = useCallback(async () => {
    setLoading(true);
    setActionError(null);
    const [offRes, blockRes] = await Promise.all([
      api.get<OffenderSummary[]>('/admin/offenders'),
      api.get<BlockedSubmitter[]>('/admin/blocks'),
    ]);
    if (offRes.error) setActionError(offRes.error);
    else if (offRes.data) setOffenders(offRes.data);
    if (blockRes.error) setActionError(blockRes.error);
    else if (blockRes.data) setBlocks(blockRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadModerationData();
  }, [loadModerationData]);

  useEffect(() => {
    setAutoBlockEnabled(settings.autoBlockEnabled ?? false);
    setAutoBlockReportThreshold(String(settings.autoBlockReportThreshold ?? 3));
  }, [settings.autoBlockEnabled, settings.autoBlockReportThreshold]);

  const handleSaveAutoBlock = async () => {
    const threshold = parseInt(autoBlockReportThreshold, 10);
    if (Number.isNaN(threshold) || threshold < 1) {
      setActionError('Threshold must be at least 1');
      return;
    }
    const ok = await onSave({
      autoBlockEnabled,
      autoBlockReportThreshold: threshold,
    });
    if (ok) setActionError(null);
  };

  const handleBlockDevice = async (deviceId: string) => {
    const reason = window.prompt('Reason for block (optional):') ?? undefined;
    const res = await api.post<BlockedSubmitter>('/admin/blocks', {
      deviceId,
      reason: reason?.trim() || 'Manually blocked by admin',
    });
    if (res.error) {
      setActionError(res.error);
      return;
    }
    await loadModerationData();
  };

  const handleUnblock = async (blockId: string) => {
    if (!confirm('Unblock this device? They will be able to submit again.')) return;
    const res = await api.delete(`/admin/blocks/${blockId}`);
    if (res.error) {
      setActionError(res.error);
      return;
    }
    await loadModerationData();
  };

  return (
    <div className="space-y-8">
      {actionError && (
        <p className="text-sm text-red-600 dark:text-red-400">{actionError}</p>
      )}

      <section>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
          Auto-block configuration
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          When enabled, a device is automatically shadow-banned after it accumulates
          the threshold number of inappropriate flags (distinct flagged submissions).
          Blocking uses device ID only; IP addresses are shown as hints only.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <Checkbox
            label="Enable auto-block"
            checked={autoBlockEnabled}
            onChange={(e) => setAutoBlockEnabled(e.target.checked)}
          />
          <div className="w-32">
            <Input
              label="Report threshold"
              type="number"
              min={1}
              max={100}
              value={autoBlockReportThreshold}
              onChange={(e) => setAutoBlockReportThreshold(e.target.value)}
              disabled={!autoBlockEnabled}
            />
          </div>
          <Button onClick={handleSaveAutoBlock} disabled={isSaving}>
            Save settings
          </Button>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Repeat offenders
          </h3>
          <Button variant="outline" size="sm" onClick={loadModerationData} disabled={loading}>
            Refresh
          </Button>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : offenders.length === 0 ? (
          <p className="text-sm text-slate-500">No repeat offenders yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Device ID</th>
                  <th className="px-3 py-2 text-left font-medium">Reports</th>
                  <th className="px-3 py-2 text-left font-medium">Submissions</th>
                  <th className="px-3 py-2 text-left font-medium">Last seen</th>
                  <th className="px-3 py-2 text-left font-medium">Related IPs (30d)</th>
                  <th className="px-3 py-2 text-left font-medium">Actions</th>
                  <th className="px-3 py-2 text-left font-medium">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {offenders.map((o) => (
                  <tr key={o.deviceId}>
                    <td className="px-3 py-2 align-middle font-mono text-xs" title={o.deviceId}>
                      {truncateDeviceId(o.deviceId)}
                    </td>
                    <td className="px-3 py-2 align-middle">{o.reportCount}</td>
                    <td className="px-3 py-2 align-middle">{o.submissionCount}</td>
                    <td className="px-3 py-2 align-middle whitespace-nowrap text-slate-500">
                      {o.lastSeen ? new Date(o.lastSeen).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 align-middle text-xs text-slate-500 max-w-[200px] truncate">
                      {o.relatedIps.length ? o.relatedIps.join(', ') : '—'}
                    </td>
                    <td className="pl-0 pr-3 py-2 align-middle">
                      {o.isBlocked && o.blockedSubmitterId ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleUnblock(o.blockedSubmitterId!)}
                        >
                          Unblock
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBlockDevice(o.deviceId)}
                        >
                          Block
                        </Button>
                      )}
                    </td>
                    <td className="pl-0 pr-3 py-2 align-middle">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={detailsButtonClassName}
                        onClick={() => setSelectedDeviceId(o.deviceId)}
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">
          Blocked submitters
        </h3>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : blocks.length === 0 ? (
          <p className="text-sm text-slate-500">No active blocks.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Device ID</th>
                  <th className="px-3 py-2 text-left font-medium">IP (hint)</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Reason</th>
                  <th className="px-3 py-2 text-left font-medium">Blocked at</th>
                  <th className="px-3 py-2 text-left font-medium">Actions</th>
                  <th className="px-3 py-2 text-left font-medium">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {blocks.map((b) => (
                  <tr key={b.id}>
                    <td className="px-3 py-2 align-middle font-mono text-xs" title={b.deviceId}>
                      {truncateDeviceId(b.deviceId)}
                    </td>
                    <td className="px-3 py-2 align-middle text-xs">{b.ipAddress ?? '—'}</td>
                    <td className="px-3 py-2 align-middle">
                      <span
                        className={cn(
                          'inline-flex rounded px-2 py-0.5 text-xs',
                          b.isAutoBlock
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                        )}
                      >
                        {b.isAutoBlock ? 'Auto' : 'Manual'}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-middle max-w-xs truncate" title={b.reason ?? ''}>
                      {b.reason ?? '—'}
                    </td>
                    <td className="px-3 py-2 align-middle whitespace-nowrap text-slate-500">
                      {new Date(b.blockedAt).toLocaleString()}
                    </td>
                    <td className="pl-0 pr-3 py-2 align-middle">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => handleUnblock(b.id)}
                      >
                        Unblock
                      </Button>
                    </td>
                    <td className="pl-0 pr-3 py-2 align-middle">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={detailsButtonClassName}
                        onClick={() => setSelectedDeviceId(b.deviceId)}
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <DeviceDetailsModal
        deviceId={selectedDeviceId}
        onClose={() => setSelectedDeviceId(null)}
      />
    </div>
  );
}
